import React, {FC, memo, useRef, useState, ReactNode} from "react";
import {Modal, ModalProps} from "antd";
import Draggable, {DraggableData, DraggableEvent} from "react-draggable";
import css from "./DraggableModal.less";

type Bounds = {
    left: number;
    top: number;
    bottom: number;
    right: number;
};

export interface DraggableModalProps extends ModalProps {
    /**
     * Aktiviert/Deaktiviert die Drag-Funktionalität
     * @default true
     */
    draggable?: boolean;
    /**
     * Padding für die Bewegungsgrenzen des Modals (links, rechts, unten).
     * Der obere Rand ist immer blockiert, damit der Titel-Bereich erreichbar bleibt.
     * Positive Werte: Modal muss innerhalb des Viewports bleiben
     * Negative Werte: Modal kann über den Rand hinaus verschoben werden
     * 0: Modal kann bis zum Rand verschoben werden
     * @default 0
     * @example
     * boundsPadding={-200} // Modal kann 200px über den Rand hinaus (links, rechts, unten)
     * boundsPadding={50}   // Modal muss 50px vom Rand entfernt bleiben
     */
    boundsPadding?: number;
    /**
     * Kinder-Elemente des Modals
     */
    children?: ReactNode;
}

/**
 * Eine erweiterte Modal-Komponente, die verschiebbar ist.
 * Basiert auf antd Modal mit react-draggable Integration.
 *
 * Das Modal kann am Titel-Bereich gezogen werden.
 *
 * @example
 * ```tsx
 * <DraggableModal
 *     title="Mein Modal"
 *     open={isOpen}
 *     onCancel={() => setIsOpen(false)}
 *     draggable={true}
 * >
 *     Inhalt hier...
 * </DraggableModal>
 * ```
 */
const DraggableModal: FC<DraggableModalProps> = ({draggable = true, boundsPadding = 300, title, children, ...modalProps}) => {
    const [disabled, setDisabled] = useState(true);
    const [bounds, setBounds] = useState<Bounds>({left: 0, top: 0, bottom: 0, right: 0});
    const draggleRef = useRef<HTMLDivElement>(null);
    const [position, setPosition] = useState({x: 0, y: 0});

    const onStart = (_event: DraggableEvent, uiData: DraggableData) => {
        const {clientWidth, clientHeight} = window.document.documentElement;
        const targetRect = draggleRef.current?.getBoundingClientRect();

        if (!targetRect) {
            return;
        }

        setBounds({
            left: -targetRect.left + uiData.x - boundsPadding,
            right: clientWidth - (targetRect.right - uiData.x) + boundsPadding,
            top: -targetRect.top + uiData.y, // Oben immer bei 0, damit Titel erreichbar bleibt
            bottom: clientHeight - (targetRect.bottom - uiData.y) + boundsPadding,
        });
    };

    const handleDoubleClick = () => {
        setPosition({x: 0, y: 0});
    };

    const handleMaskDoubleClick = (e: React.MouseEvent) => {
        // Nur wenn direkt auf die Maske geklickt wurde (nicht auf das Modal selbst)
        if (e.target === e.currentTarget) {
            handleDoubleClick();
        }
    };

    // Wenn draggable deaktiviert ist, normales Modal zurückgeben
    if (!draggable) {
        return (
            <Modal title={title} {...modalProps}>
                {children}
            </Modal>
        );
    }

    return (
        <Modal
            maskStyle={{cursor: "pointer"}}
            wrapProps={{onDoubleClick: handleMaskDoubleClick}}
            title={
                <div
                    data-testid="draggable-modal-title"
                    className={css.draggableTitle}
                    onMouseOver={() => {
                        if (disabled) {
                            setDisabled(false);
                        }
                    }}
                    onMouseOut={() => {
                        setDisabled(true);
                    }}
                    onDoubleClick={handleDoubleClick}
                    onFocus={() => undefined}
                    onBlur={() => undefined}
                >
                    {title}
                </div>
            }
            modalRender={(modal) => (
                <Draggable
                    disabled={disabled}
                    bounds={bounds}
                    nodeRef={draggleRef}
                    onStart={(event, uiData) => onStart(event, uiData)}
                    position={position}
                    onStop={(_e, data) => setPosition({x: data.x, y: data.y})}
                >
                    <div ref={draggleRef}>{modal}</div>
                </Draggable>
            )}
            {...modalProps}
        >
            {children}
        </Modal>
    );
};

export default memo(DraggableModal);
